<!--
  IconButton.vue

  This component represents a customizable icon button with optional text. 

  Props:
    - text: Optional text to display on the button.
    - iconHref: Required path to the SVG icon to display on the button.
    - onClick: Optional function to execute when the button is clicked.
    - disabled: Optional boolean to disable the button. Default is false.

  The component uses scoped SCSS for styling. The button's appearance changes when hovered over or disabled.

  The button emits a 'click' event when clicked, which can be listened to by parent components.
-->
<template>
  <div
    :class="`icon-button ${text ? 'text-button' : 'icon-only-button'} ${iconPosition}`"
    role="button"
    :disabled="disabled"
    @click="onClick"
    @keydown.enter="enterDown"
    aria-label="icon button"
  >
    <div v-if="text">{{ text }}</div>
    <div class="icon-container">
      <svg>
        <use :xlink:href="iconHref" />
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IconButton',
  props: {
    text: {
      type: String,
      required: false,
    },
    iconHref: {
      type: String,
      required: true,
    },
    iconPosition: {
      type: String,
      default: 'right',
      validator(value) {
        // The iconPosition must be one of these
        return ['left', 'right'].includes(value);
      },
    },
    onClick: {
      type: Function,
      required: false,
      default: () => {},
    },
    enterDown: {
      type: Function,
      required: false,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    handleClick() {
      this.$emit('click');
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-button {
  cursor: pointer;
  border: none;
  background-color: transparent;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: fit-content;
  margin: 12px 16px;

  &.left {
    flex-direction: row-reverse;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    svg {
      height: 24px;
      width: 24px;
      color: inherit;

      @include mobile {
        height: 18px;
        width: 18px;
      }
    }

    use {
      color: inherit;
    }
  }

  &[disabled]:not([disabled='false']) {
    cursor: not-allowed !important;
  }
}

.text-button {
  padding: 4px 8px;
  min-width: 156px;
  border-radius: 8px;
  color: $CRIMSON;
  text-align: center;
  font-family: inherit;
  font-size: $DESKTOP_H7_SIZE;
  font-weight: $BOLD;
  column-gap: 8px;

  &:hover {
    background-color: $CRIMSON_TINT_50;
  }

  &[disabled]:not([disabled='false']) {
    color: $DISABLED !important;
    background-color: $GREY_100 !important;
  }
}

.icon-only-button {
  color: inherit;
  border-radius: 50%;

  @media (hover: hover) {
    &:hover {
      background-color: $PRIMARY_GREEN_100;

      svg {
        color: $NEUTRAL_WHITE;
      }
    }
  }

  &[disabled]:not([disabled='false']) {
    color: $GREY_600 !important;
  }
}

// DHDS search icon button (Figma: 46px hit-area, 34px hover bg, 18px icon, 1.5px focus ring)
.icon-button.search-small-screen.icon-only-button {
  width: 46px;
  height: 46px;
  padding: 0;
  border-radius: 9999px;
  color: $PRIMARY_GREEN_100;

  .icon-container {
    width: 34px;
    height: 34px;
    border-radius: 9999px;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  // Keep this aligned with Figma "State=focused" (ring only; no filled bg)
  &:focus-visible .icon-container::after {
    content: '';
    position: absolute;
    inset: -4px;
    border: 1.5px solid $PRIMARY_GREEN_100;
    border-radius: 9999px;
    pointer-events: none;
  }

  @media (hover: hover) {
    &:hover {
      background-color: transparent;

      .icon-container {
        background-color: $PRIMARY_EGGSHELL_40;
      }

      svg {
        color: $PRIMARY_MEDIUM_GREEN_100;
      }
    }
  }
}
</style>
